// "Keep, grow or cut" — a standalone value-map view for admins.
//
// Deliberately built on illustrative/imagined numbers, not live platform data:
// this is a template for the kind of team-triage view the org could build once
// a real "value" signal (active use vs. shipped work) is trusted enough to act
// on automatically. Swap DUMMY_TEAMS for a real data source when that's ready.

import { useState } from "react";

const DUMMY_TEAMS = [
  {
    name: "Client Retail",
    tag: "RTL-221",
    active: 91, moving: 66, shipped: 58,
    badge: "Over budget", badgeColor: "var(--warning)", badgeBg: "var(--warning-light)",
    suggestion: "Raise budget, bill the client",
  },
  {
    name: "Data Engineering",
    active: 92, moving: 78, shipped: 64,
    badge: "Working well", badgeColor: "var(--success)", badgeBg: "var(--success-light)",
    suggestion: "Renew",
  },
  {
    name: "Platform",
    active: 84, moving: 70, shipped: 55,
    badge: "Working well", badgeColor: "var(--success)", badgeBg: "var(--success-light)",
    suggestion: "Renew",
  },
  {
    name: "Retail",
    active: 78, moving: 60, shipped: 47,
    badge: "Working well", badgeColor: "var(--success)", badgeBg: "var(--success-light)",
    suggestion: "Renew",
  },
  {
    name: "Design",
    active: 72, moving: 58, shipped: 40,
    badge: "Working well", badgeColor: "var(--success)", badgeBg: "var(--success-light)",
    suggestion: "Renew",
  },
  {
    name: "Analytics",
    active: 58, moving: 40, shipped: 30,
    badge: "Busy, not moving", badgeColor: "var(--warning)", badgeBg: "var(--warning-light)",
    suggestion: "Coach the team, recheck in 30 days",
  },
  {
    name: "Marketing",
    active: 40, moving: 12, shipped: 8,
    badge: "Not used", badgeColor: "var(--danger)", badgeBg: "var(--danger-light)",
    suggestion: "Move to Chat level",
  },
];

function ValueMap({ teams }) {
  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 3 }}>Value map</div>
      <div style={{ fontSize: 11.5, color: "var(--muted)", marginBottom: 14 }}>
        How active each team is, against how much its work is moving.
      </div>

      <div style={{ position: "relative", height: 320, border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
        {/* quadrant backgrounds */}
        <div style={{ position: "absolute", inset: 0, display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr" }}>
          <div style={{ background: "var(--bg)" }} />
          <div style={{ background: "var(--success-light)" }} />
          <div style={{ background: "var(--bg)" }} />
          <div style={{ background: "var(--warning-light)" }} />
        </div>
        {/* corner labels */}
        <span style={{ position: "absolute", top: 10, left: 12, fontSize: 11, fontWeight: 600, color: "var(--muted)" }}>Moving, light use</span>
        <span style={{ position: "absolute", top: 10, right: 12, fontSize: 11, fontWeight: 600, color: "var(--success)" }}>Working well</span>
        <span style={{ position: "absolute", bottom: 10, left: 12, fontSize: 11, fontWeight: 600, color: "var(--muted)" }}>Not used</span>
        <span style={{ position: "absolute", bottom: 10, right: 12, fontSize: 11, fontWeight: 600, color: "var(--warning)" }}>Busy, not moving</span>
        {/* midlines */}
        <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 1, background: "var(--border)" }} />
        <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: "var(--border)" }} />

        {/* team markers */}
        {teams.map((t) => (
          <div
            key={t.name}
            title={`${t.name} — Active ${t.active}%, Moving ${t.moving}%`}
            style={{
              position: "absolute",
              left: `${Math.min(Math.max(t.active, 3), 97)}%`,
              bottom: `${Math.min(Math.max(t.moving, 3), 97)}%`,
              transform: "translate(-50%, 50%)",
              display: "flex", alignItems: "center", gap: 5,
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 600, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 5, padding: "2px 6px" }}>
              {t.name}
            </span>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#111", flexShrink: 0 }} />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 11, color: "var(--muted)" }}>
        <span>↑ Work moving</span>
        <span>Activity →</span>
      </div>
    </div>
  );
}

function SuggestionCard({ team, decision, onDecide }) {
  return (
    <div className="card" style={{ padding: "16px 18px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div>
          <div style={{ fontSize: 14.5, fontWeight: 600 }}>
            {team.name}{team.tag ? ` · ${team.tag}` : ""}
          </div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>
            Active {team.active}% · Moving {team.moving}% · Shipped {team.shipped}%
          </div>
        </div>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: team.badgeColor, background: team.badgeBg, borderRadius: 6, padding: "3px 9px", whiteSpace: "nowrap" }}>
          {team.badge}
        </span>
      </div>

      <div style={{ borderTop: "1px solid var(--border)", marginTop: 12, paddingTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {decision ? (
          <>
            <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
              {decision === "accept" ? "Accepted: " : "Kept as is — suggested: "}
              <b style={{ color: "var(--text)" }}>{team.suggestion}</b>
            </div>
            <button
              onClick={() => onDecide(team.name, null)}
              style={{ fontFamily: "inherit", fontSize: 12, color: "var(--muted)", textDecoration: "underline", cursor: "pointer", background: "none", border: "none", padding: 0 }}
            >
              Change
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13 }}>Suggested: <b>{team.suggestion}</b></div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => onDecide(team.name, "accept")}
                style={{
                  fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, padding: "7px 14px", borderRadius: 7,
                  border: "1px solid var(--success)", background: "var(--success)", color: "#fff", cursor: "pointer",
                }}
              >
                Accept
              </button>
              <button
                onClick={() => onDecide(team.name, "keep")}
                style={{
                  fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, padding: "7px 14px", borderRadius: 7,
                  border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", cursor: "pointer",
                }}
              >
                Keep as is
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function KeepGrowCut() {
  // Local-only for now — this page runs on imagined data, so decisions aren't
  // persisted anywhere; they just reflect back in the UI for this session.
  const [decisions, setDecisions] = useState({});

  const handleDecide = (name, action) => {
    setDecisions((prev) => {
      const next = { ...prev };
      if (action) next[name] = action;
      else delete next[name];
      return next;
    });
  };

  return (
    <>
      <div className="page-header">
        <h1>Keep, grow or cut</h1>
        <p>Each team gets one suggestion. Accept it, and limits and access update in Claude automatically.</p>
      </div>

      <div className="page-body">
        <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 1fr) minmax(340px, 1.4fr)", gap: 16, alignItems: "start" }}>
          <ValueMap teams={DUMMY_TEAMS} />

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {DUMMY_TEAMS.map((t) => (
              <SuggestionCard key={t.name} team={t} decision={decisions[t.name]} onDecide={handleDecide} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
